import { Fragment, ReactNode, useEffect, useState } from 'react'
import { buildLogRocketIdentifyPayload } from '@/shared/lib/logrocket-identify'
import logoUrl from '@/shared/assets/logo.svg'
import { Link, useForm, usePage } from '@inertiajs/react'
import { BreadcrumbsProvider, useBreadcrumbItems } from '@/shared/lib/breadcrumbs-context'
import type { LucideIcon } from 'lucide-react'
import {
  Activity,
  AlertTriangle,
  Bell,
  BookOpen,
  ChevronDown,
  ClipboardCheck,
  ExternalLink,
  FileText,
  Gauge,
  GitBranch,
  HeadphonesIcon,
  LogOut,
  MenuIcon,
  Microscope,
  ScrollText,
  Settings,
  Shapes,
  Tag,
  UserCircle,
  Users,
  XIcon,
} from 'lucide-react'
import { toast, Toaster } from 'sonner'
import { t } from '@/shared/lib/i18n'
import { showErrorToast } from '@/shared/lib/toast'
import {
  adminInstrumentTagsPath,
  adminInstrumentTypesPath,
  adminLoggersPath,
  adminSettingsPath,
  adminSignoffMonitoringPath,
  adminSubscriptionPath,
  adminUsersPath,
  approvalsPath,
  complianceDashboardPath,
  departmentsPath,
  instrumentsPath,
  nonComplianceDashboardPath,
  tasksDashboardPath,
  notificationPreferencePath,
  profilePath,
  organizationPath,
  reportsDepartmentsPath,
  reportsDeletedHistoryPath,
  reportsInstrumentsPath,
  reportsMaintenanceLogsPath,
  reportsMonthlyPath,
  reportsSitesPath,
  reportsUnitsPath,
  reportsUsersPath,
  rootPath,
  sessionPath,
  sitesPath,
  unitsPath,
} from '@/shared/lib/routes'
import { cn } from '@/shared/lib/utils'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import type { SharedProps } from '@/shared/types/SharedProps'
import { Button } from '@/shared/ui/button'
import { Sheet, SheetClose, SheetContent, SheetTrigger } from '@/shared/ui/sheet'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ChevronDownIcon,
} from '@/shared/ui/dropdown-menu'
import { CustomerSwitcher, CustomerSwitcherMobileList } from '@/shared/ui/CustomerSwitcher'
import { HeaderScanButton } from '@/features/header-scan-button'
import { ImpersonationBanner } from '@/features/impersonation-banner'
import { TenantAdminBanner } from '@/features/tenant-admin-banner'
import { SessionTimeoutModal } from '@/features/session-timeout'

function Breadcrumbs() {
  const items = useBreadcrumbItems()
  if (items.length === 0) return null
  return (
    <div className="border-b bg-muted/30">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <nav aria-label="Breadcrumb" className="flex h-9 items-center gap-1 text-sm">
          {items.map((item, i) => {
            const isLast = i === items.length - 1
            return (
              <Fragment key={i}>
                {i > 0 && <span className="text-muted-foreground/60 select-none">/</span>}
                {item.href && !isLast ? (
                  <Link
                    href={item.href}
                    className="text-muted-foreground hover:text-foreground transition-colors truncate max-w-[14rem]"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span
                    className={
                      isLast
                        ? 'font-medium text-foreground truncate max-w-[14rem]'
                        : 'text-muted-foreground truncate max-w-[14rem]'
                    }
                  >
                    {item.label}
                  </span>
                )}
              </Fragment>
            )
          })}
        </nav>
      </div>
    </div>
  )
}

interface AppLayoutProps {
  children: ReactNode
}

const AppLayout = ({ children }: AppLayoutProps) => {
  const {
    props: { currentUserInfo, flash, appInfo, customerSwitcher, onSignoffChain },
    url,
  } = usePage<SharedProps>()
  const logoutForm = useForm({})
  const canReadNotificationPreferences = useCan(Resources.NOTIFICATION_PREFERENCES, Actions.READ)
  const canReadProfile = useCan(Resources.PROFILE, Actions.READ)
  const canReadInstruments = useCan(Resources.INSTRUMENT_MAINTENANCE, Actions.READ)
  const isAdminOrUserPlus = useCan(Resources.RBAC, Actions.READ)
  const canReadSignoffChain = useCan(Resources.SIGNOFF_CHAIN, Actions.READ)
  const canReadCompliance = useCan(Resources.COMPLIANCE_REPORT, Actions.READ)
  // Dashboard Signatures nav follows chain membership (REBUILD-596); Sign-off Monitoring
  // admin links still use canReadSignoffChain.
  const showSignaturesNav = onSignoffChain === true

  // Trigger toasts on the flash `nonce` (a fresh id per response that carries a message),
  // not on the message string. This re-fires for identical consecutive messages — e.g.
  // adding the same comment twice in a row — which a value-based effect would skip
  // (REBUILD-157).
  useEffect(() => {
    if (flash?.notice) toast.success(flash.notice)
    if (flash?.alert) showErrorToast(flash.alert)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flash?.nonce])

  useEffect(() => {
    if (
      ['development', 'test'].includes(appInfo.environment) ||
      !appInfo.logrocketAppId ||
      !currentUserInfo
    )
      return
    // Capture values before the async import so stale closures can't occur.
    const userId = String(currentUserInfo.id)
    const traits = buildLogRocketIdentifyPayload(currentUserInfo, customerSwitcher)
    // Dynamic import keeps LogRocket out of the dev bundle; module is cached
    // after the first call so subsequent identify() calls resolve synchronously.
    import('logrocket').then(({ default: LogRocket }) => {
      LogRocket.identify(userId, traits)
    })
    // Re-identify on user change or tenant switch; name/email changes don't
    // warrant a re-identify (captured correctly at switch time via traits above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserInfo?.id, customerSwitcher?.currentCustomerId])
  const canReadReports = useCan(Resources.INSTRUMENT_REPORTS, Actions.READ)
  const canReadInstrumentTags = useCan(Resources.ADMIN_INSTRUMENT_TAG, Actions.READ)
  const canReadInstrumentTypes = useCan(Resources.ADMIN_INSTRUMENT_TYPE, Actions.READ)
  const showCatalogMenu = isAdminOrUserPlus

  const isUsersSectionActive =
    url.startsWith(adminUsersPath()) || url.startsWith(adminLoggersPath())

  const isUsersOrSystemActive =
    isUsersSectionActive ||
    url.startsWith(adminSettingsPath()) ||
    url.startsWith(adminSubscriptionPath())

  const isCatalogActive = url.startsWith(adminInstrumentTypesPath())

  const isReportsActive =
    url.startsWith(reportsMonthlyPath()) ||
    url.startsWith(reportsDeletedHistoryPath()) ||
    url.startsWith(reportsSitesPath()) ||
    url.startsWith(reportsDepartmentsPath()) ||
    url.startsWith(reportsUnitsPath()) ||
    url.startsWith(reportsInstrumentsPath()) ||
    url.startsWith(reportsMaintenanceLogsPath()) ||
    url.startsWith(reportsUsersPath()) ||
    url.startsWith(complianceDashboardPath())

  const isOrganizationActive =
    url.startsWith(organizationPath()) ||
    url.startsWith(sitesPath()) ||
    url.startsWith(departmentsPath()) ||
    url.startsWith(unitsPath()) ||
    (canReadSignoffChain && url.startsWith(adminSignoffMonitoringPath()))

  const isDashboardActive =
    url.startsWith(tasksDashboardPath()) ||
    url.startsWith(nonComplianceDashboardPath()) ||
    url.startsWith(approvalsPath())
  const isInstrumentsActive = url.startsWith(instrumentsPath())
  const isInstrumentTagsActive = url.startsWith(adminInstrumentTagsPath())
  const isAdministrationActive =
    isUsersOrSystemActive || isCatalogActive || isOrganizationActive || isInstrumentTagsActive

  const [isReportsOpen, setIsReportsOpen] = useState(isReportsActive)
  const [isAdminOpen, setIsAdminOpen] = useState(isAdministrationActive)

  useEffect(() => {
    setIsReportsOpen(isReportsActive)
  }, [isReportsActive])
  useEffect(() => {
    setIsAdminOpen(isAdministrationActive)
  }, [isAdministrationActive])

  const handleLogout = () => {
    logoutForm.delete(sessionPath())
  }

  const navLinkStateClass = (href: string, exact = false) => {
    const isActive = href === '/' ? url === '/' : exact ? url === href : url.startsWith(href)
    return isActive ? 'font-medium text-primary' : 'text-muted-foreground hover:text-primary'
  }

  const navLinkClass = (href: string, exact = false) =>
    cn('text-sm', navLinkStateClass(href, exact))

  const isNavActive = (href: string, exact = false) =>
    href === '/' ? url === '/' : exact ? url === href : url.startsWith(href)

  const mobileRowClass = (href: string, exact = false) =>
    cn(
      'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[15px] leading-snug transition-colors sm:py-3',
      isNavActive(href, exact)
        ? 'bg-primary/10 font-medium text-primary'
        : 'text-foreground/90 hover:bg-muted/80 active:bg-muted/90',
    )

  const dashboardLinks: {
    href: string
    label: string
    icon: LucideIcon
    exact?: boolean
  }[] = [
    {
      href: tasksDashboardPath(),
      label: t('nav.tasksDashboard'),
      icon: ScrollText,
      exact: true,
    },
    ...(canReadCompliance
      ? [
          {
            href: nonComplianceDashboardPath(),
            label: t('nav.nonCompliance'),
            icon: AlertTriangle,
          },
        ]
      : []),
    ...(showSignaturesNav
      ? [
          {
            href: approvalsPath(),
            label: t('nav.pendingApprovals'),
            icon: ClipboardCheck,
          },
        ]
      : []),
  ]

  const instrumentsLinks: { href: string; label: string; icon: LucideIcon; exact?: boolean }[] = [
    { href: instrumentsPath(), label: t('nav.instruments'), icon: Microscope, exact: true },
  ]

  const organizationLinks: { href: string; label: string; icon: LucideIcon }[] = [
    { href: organizationPath(), label: t('nav.organization'), icon: GitBranch },
  ]

  const catalogLinks: { href: string; label: string; icon: LucideIcon }[] = canReadInstrumentTypes
    ? [{ href: adminInstrumentTypesPath(), label: t('nav.instrumentTypes'), icon: Shapes }]
    : []

  return (
    <BreadcrumbsProvider>
      <div className="min-h-screen bg-background">
        <nav className="bg-primary px-4 py-3 sm:px-6">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-4 lg:gap-6">
              <Link
                href={rootPath()}
                className="shrink-0 text-lg font-bold tracking-tight text-white"
              >
                Lab Logs
              </Link>

              <div className="hidden min-w-0 items-center gap-4 lg:flex">
                {/* Dashboard */}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    className={cn(
                      'flex cursor-pointer items-center gap-1 text-sm outline-none',
                      isDashboardActive
                        ? 'font-medium text-white'
                        : 'text-white/70 hover:text-white',
                    )}
                  >
                    {t('nav.dashboard')}
                    <ChevronDownIcon className="h-3.5 w-3.5" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {dashboardLinks.map((item) => (
                      <DropdownMenuItem key={item.href} asChild>
                        <Link
                          href={item.href}
                          className={cn(
                            'w-full flex items-center gap-1',
                            navLinkClass(item.href, item.exact),
                          )}
                        >
                          {item.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {canReadReports && (
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      className={cn(
                        'flex cursor-pointer items-center gap-1 text-sm outline-none',
                        isReportsActive
                          ? 'font-medium text-white'
                          : 'text-white/70 hover:text-white',
                      )}
                    >
                      {t('nav.reports')}
                      <ChevronDownIcon className="h-3.5 w-3.5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem asChild>
                        <Link
                          href={reportsMonthlyPath()}
                          className={cn('w-full', navLinkClass(reportsMonthlyPath()))}
                        >
                          {t('nav.monthlyReports')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link
                          href={reportsDeletedHistoryPath()}
                          className={cn('w-full', navLinkClass(reportsDeletedHistoryPath()))}
                        >
                          {t('nav.deletedHistory')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link
                          href={reportsSitesPath()}
                          className={cn('w-full', navLinkClass(reportsSitesPath()))}
                        >
                          {t('nav.sitesReport')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link
                          href={reportsDepartmentsPath()}
                          className={cn('w-full', navLinkClass(reportsDepartmentsPath()))}
                        >
                          {t('nav.departmentsReport')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link
                          href={reportsUnitsPath()}
                          className={cn('w-full', navLinkClass(reportsUnitsPath()))}
                        >
                          {t('nav.unitsReport')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link
                          href={reportsInstrumentsPath()}
                          className={cn('w-full', navLinkClass(reportsInstrumentsPath()))}
                        >
                          {t('nav.instrumentsReport')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link
                          href={reportsMaintenanceLogsPath()}
                          className={cn('w-full', navLinkClass(reportsMaintenanceLogsPath()))}
                        >
                          {t('nav.maintenanceLogsReport')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link
                          href={reportsUsersPath()}
                          className={cn('w-full', navLinkClass(reportsUsersPath()))}
                        >
                          {t('nav.usersReport')}
                        </Link>
                      </DropdownMenuItem>
                      {canReadCompliance && (
                        <DropdownMenuItem asChild>
                          <Link
                            href={complianceDashboardPath()}
                            className={cn('w-full', navLinkClass(complianceDashboardPath()))}
                          >
                            {t('nav.complianceReviews')}
                          </Link>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Instruments */}
                <Link
                  href={instrumentsPath()}
                  className={cn(
                    'flex cursor-pointer items-center gap-1 text-sm outline-none',
                    isInstrumentsActive
                      ? 'font-medium text-white'
                      : 'text-white/70 hover:text-white',
                  )}
                >
                  {t('nav.instruments')}
                </Link>

                {/* Administration */}
                {showCatalogMenu && (
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      className={cn(
                        'flex cursor-pointer items-center gap-1 text-sm outline-none',
                        isAdministrationActive
                          ? 'font-medium text-white'
                          : 'text-white/70 hover:text-white',
                      )}
                    >
                      {t('nav.administration')}
                      <ChevronDownIcon className="h-3.5 w-3.5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {/* Organization */}
                      {organizationLinks.map((item) => (
                        <DropdownMenuItem key={item.href} asChild>
                          <Link href={item.href} className={cn('w-full', navLinkClass(item.href))}>
                            {item.label}
                          </Link>
                        </DropdownMenuItem>
                      ))}

                      {/* Instrument Types */}
                      {showCatalogMenu &&
                        catalogLinks.map((item) => (
                          <DropdownMenuItem key={item.href} asChild>
                            <Link
                              href={item.href}
                              className={cn('w-full', navLinkClass(item.href))}
                            >
                              {item.label}
                            </Link>
                          </DropdownMenuItem>
                        ))}

                      {/* Tags */}
                      {canReadInstrumentTags && (
                        <DropdownMenuItem asChild>
                          <Link
                            href={adminInstrumentTagsPath()}
                            className={cn('w-full', navLinkClass(adminInstrumentTagsPath()))}
                          >
                            {t('nav.instrumentTags')}
                          </Link>
                        </DropdownMenuItem>
                      )}

                      {/* Users */}
                      {isAdminOrUserPlus && (
                        <DropdownMenuItem asChild>
                          <Link
                            href={adminUsersPath()}
                            className={cn(
                              'w-full',
                              isUsersSectionActive
                                ? 'font-medium text-primary'
                                : 'text-muted-foreground hover:text-primary',
                            )}
                          >
                            {t('nav.users')}
                          </Link>
                        </DropdownMenuItem>
                      )}

                      {/* System Settings + Subscription + Sign-off Monitoring */}
                      {isAdminOrUserPlus && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link
                              href={adminSettingsPath()}
                              className={cn('w-full', navLinkClass(adminSettingsPath()))}
                            >
                              {t('nav.systemSettings')}
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link
                              href={adminSubscriptionPath()}
                              className={cn('w-full', navLinkClass(adminSubscriptionPath()))}
                            >
                              {t('nav.subscription')}
                            </Link>
                          </DropdownMenuItem>
                          {canReadSignoffChain && (
                            <DropdownMenuItem asChild>
                              <Link
                                href={adminSignoffMonitoringPath()}
                                className={cn('w-full', navLinkClass(adminSignoffMonitoringPath()))}
                              >
                                {t('nav.signoffMonitoring')}
                              </Link>
                            </DropdownMenuItem>
                          )}
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1 lg:gap-2">
              {currentUserInfo && canReadInstruments && <HeaderScanButton />}
              {currentUserInfo && (
                <div className="hidden min-w-0 items-center gap-2 sm:gap-3 lg:flex">
                  <CustomerSwitcher />
                  <DropdownMenu>
                    <DropdownMenuTrigger className="flex shrink-0 cursor-pointer items-center gap-1 text-sm text-white/70 transition-colors hover:text-white">
                      <span className="max-w-[16rem] truncate">{currentUserInfo.name}</span>
                      <ChevronDownIcon className="size-4 shrink-0" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[10rem]">
                      {canReadProfile ? (
                        <DropdownMenuItem asChild>
                          <Link href={profilePath()}>{t('nav.myProfile')}</Link>
                        </DropdownMenuItem>
                      ) : null}
                      {canReadNotificationPreferences && (
                        <DropdownMenuItem asChild>
                          <Link href={notificationPreferencePath()}>
                            {t('nav.notificationPreferences')}
                          </Link>
                        </DropdownMenuItem>
                      )}
                      {appInfo?.releaseNotesUrl && (
                        <DropdownMenuItem asChild>
                          <a
                            href={appInfo.releaseNotesUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t('nav.releaseNotes', { version: appInfo.version })}
                            <ExternalLink
                              className="ml-2 size-3.5 shrink-0 text-muted-foreground"
                              aria-hidden
                            />
                          </a>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem asChild>
                        {appInfo.contactUsUrl.startsWith('/') ? (
                          <Link href={appInfo.contactUsUrl}>{t('nav.needHelpContactUs')}</Link>
                        ) : (
                          <a href={appInfo.contactUsUrl} target="_blank" rel="noopener noreferrer">
                            {t('nav.needHelpContactUs')}
                            <ExternalLink
                              className="ml-2 size-3.5 shrink-0 text-muted-foreground"
                              aria-hidden
                            />
                          </a>
                        )}
                      </DropdownMenuItem>
                      {appInfo?.helpDocumentationUrl && (
                        <DropdownMenuItem asChild>
                          <a
                            href={appInfo.helpDocumentationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t('nav.helpDocs')}
                            <ExternalLink
                              className="ml-2 size-3.5 shrink-0 text-muted-foreground"
                              aria-hidden
                            />
                          </a>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={handleLogout}
                        disabled={logoutForm.processing}
                        className="text-destructive focus:text-destructive"
                      >
                        {t('nav.signOut')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}

              <div className="lg:hidden">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0 text-white/70 hover:bg-white/10 hover:text-white"
                      aria-label={t('nav.openMenu')}
                    >
                      <MenuIcon className="size-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent
                    side="right"
                    hideCloseButton
                    className="w-[min(100vw,22rem)] max-w-full overflow-hidden border-l bg-white p-0"
                    title={t('nav.mainMenuTitle')}
                    description={t('nav.mainMenuDescription')}
                  >
                    <div className="flex min-h-0 w-full flex-1 flex-col">
                      <div className="flex min-h-11 shrink-0 items-center justify-between gap-2 border-b border-border/80 px-3 pb-3 pt-3 [padding-top:max(0.75rem,env(safe-area-inset-top))] sm:px-4">
                        <SheetClose asChild>
                          <Link
                            href={rootPath()}
                            className="min-w-0 flex-1 py-1 pr-1 text-left no-underline outline-none"
                          >
                            <img src={logoUrl} alt={t('nav.appName')} className="h-7 w-auto" />
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="shrink-0 text-muted-foreground hover:text-foreground"
                            aria-label={t('nav.closeMenu')}
                          >
                            <XIcon className="size-5" />
                          </Button>
                        </SheetClose>
                      </div>

                      <div
                        className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-3 py-1 sm:px-4"
                        role="navigation"
                        aria-label={t('nav.mainMenuTitle')}
                      >
                        <div className="space-y-0.5">
                          {/* Dashboard */}
                          <div className="pt-3">
                            <p className="px-1 pb-2 text-[0.7rem] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                              {t('nav.dashboard')}
                            </p>
                            <div className="space-y-0.5">
                              {dashboardLinks.map((item) => {
                                const Icon = item.icon
                                const active = isNavActive(item.href, item.exact)
                                return (
                                  <SheetClose key={item.href} asChild>
                                    <Link
                                      href={item.href}
                                      className={mobileRowClass(item.href, item.exact)}
                                    >
                                      <Icon
                                        className={cn(
                                          'size-5 shrink-0',
                                          active ? 'text-primary' : 'text-muted-foreground',
                                        )}
                                        strokeWidth={active ? 2.25 : 1.75}
                                        aria-hidden
                                      />
                                      <span className="min-w-0 flex-1 leading-snug">
                                        {item.label}
                                      </span>
                                    </Link>
                                  </SheetClose>
                                )
                              })}
                            </div>
                          </div>

                          {/* Instruments */}
                          <div className="mt-4 border-t border-border/80 pt-3">
                            <p className="px-1 pb-2 text-[0.7rem] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                              {t('nav.instruments')}
                            </p>
                            <div className="space-y-0.5">
                              {instrumentsLinks.map((item) => {
                                const Icon = item.icon
                                const active = isNavActive(item.href, item.exact)
                                return (
                                  <SheetClose key={item.href} asChild>
                                    <Link
                                      href={item.href}
                                      className={mobileRowClass(item.href, item.exact)}
                                    >
                                      <Icon
                                        className={cn(
                                          'size-5 shrink-0',
                                          active ? 'text-primary' : 'text-muted-foreground',
                                        )}
                                        strokeWidth={active ? 2.25 : 1.75}
                                        aria-hidden
                                      />
                                      <span className="min-w-0 flex-1 leading-snug">
                                        {item.label}
                                      </span>
                                    </Link>
                                  </SheetClose>
                                )
                              })}
                              {canReadInstruments && (
                                <div className="px-1 pt-2">
                                  <HeaderScanButton mobile />
                                </div>
                              )}
                            </div>
                          </div>

                          {canReadReports && (
                            <div className="mt-4 border-t border-border/80 pt-3">
                              <Collapsible open={isReportsOpen} onOpenChange={setIsReportsOpen}>
                                <CollapsibleTrigger asChild>
                                  <button
                                    className={cn(
                                      'flex w-full items-center justify-between px-1 pb-2 text-[0.7rem] font-semibold tracking-[0.12em] uppercase outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded',
                                      isReportsActive ? 'text-primary' : 'text-muted-foreground',
                                    )}
                                  >
                                    {t('nav.reports')}
                                    <ChevronDown
                                      className={cn(
                                        'size-3.5 shrink-0 transition-transform',
                                        isReportsOpen && 'rotate-180',
                                      )}
                                      aria-hidden
                                    />
                                  </button>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="space-y-0.5">
                                    <SheetClose asChild>
                                      <Link
                                        href={reportsMonthlyPath()}
                                        className={mobileRowClass(reportsMonthlyPath())}
                                      >
                                        <FileText
                                          className={cn(
                                            'size-5 shrink-0',
                                            isNavActive(reportsMonthlyPath())
                                              ? 'text-primary'
                                              : 'text-muted-foreground',
                                          )}
                                          strokeWidth={
                                            isNavActive(reportsMonthlyPath()) ? 2.25 : 1.75
                                          }
                                          aria-hidden
                                        />
                                        <span className="min-w-0 flex-1 leading-snug">
                                          {t('nav.monthlyReports')}
                                        </span>
                                      </Link>
                                    </SheetClose>
                                    <SheetClose asChild>
                                      <Link
                                        href={reportsDeletedHistoryPath()}
                                        className={mobileRowClass(reportsDeletedHistoryPath())}
                                      >
                                        <FileText
                                          className={cn(
                                            'size-5 shrink-0',
                                            isNavActive(reportsDeletedHistoryPath())
                                              ? 'text-primary'
                                              : 'text-muted-foreground',
                                          )}
                                          strokeWidth={
                                            isNavActive(reportsDeletedHistoryPath()) ? 2.25 : 1.75
                                          }
                                          aria-hidden
                                        />
                                        <span className="min-w-0 flex-1 leading-snug">
                                          {t('nav.deletedHistory')}
                                        </span>
                                      </Link>
                                    </SheetClose>
                                    <SheetClose asChild>
                                      <Link
                                        href={reportsSitesPath()}
                                        className={mobileRowClass(reportsSitesPath())}
                                      >
                                        <FileText
                                          className={cn(
                                            'size-5 shrink-0',
                                            isNavActive(reportsSitesPath())
                                              ? 'text-primary'
                                              : 'text-muted-foreground',
                                          )}
                                          strokeWidth={
                                            isNavActive(reportsSitesPath()) ? 2.25 : 1.75
                                          }
                                          aria-hidden
                                        />
                                        <span className="min-w-0 flex-1 leading-snug">
                                          {t('nav.sitesReport')}
                                        </span>
                                      </Link>
                                    </SheetClose>
                                    <SheetClose asChild>
                                      <Link
                                        href={reportsDepartmentsPath()}
                                        className={mobileRowClass(reportsDepartmentsPath())}
                                      >
                                        <FileText
                                          className={cn(
                                            'size-5 shrink-0',
                                            isNavActive(reportsDepartmentsPath())
                                              ? 'text-primary'
                                              : 'text-muted-foreground',
                                          )}
                                          strokeWidth={
                                            isNavActive(reportsDepartmentsPath()) ? 2.25 : 1.75
                                          }
                                          aria-hidden
                                        />
                                        <span className="min-w-0 flex-1 leading-snug">
                                          {t('nav.departmentsReport')}
                                        </span>
                                      </Link>
                                    </SheetClose>
                                    <SheetClose asChild>
                                      <Link
                                        href={reportsUnitsPath()}
                                        className={mobileRowClass(reportsUnitsPath())}
                                      >
                                        <FileText
                                          className={cn(
                                            'size-5 shrink-0',
                                            isNavActive(reportsUnitsPath())
                                              ? 'text-primary'
                                              : 'text-muted-foreground',
                                          )}
                                          strokeWidth={
                                            isNavActive(reportsUnitsPath()) ? 2.25 : 1.75
                                          }
                                          aria-hidden
                                        />
                                        <span className="min-w-0 flex-1 leading-snug">
                                          {t('nav.unitsReport')}
                                        </span>
                                      </Link>
                                    </SheetClose>
                                    <SheetClose asChild>
                                      <Link
                                        href={reportsInstrumentsPath()}
                                        className={mobileRowClass(reportsInstrumentsPath())}
                                      >
                                        <FileText
                                          className={cn(
                                            'size-5 shrink-0',
                                            isNavActive(reportsInstrumentsPath())
                                              ? 'text-primary'
                                              : 'text-muted-foreground',
                                          )}
                                          strokeWidth={
                                            isNavActive(reportsInstrumentsPath()) ? 2.25 : 1.75
                                          }
                                          aria-hidden
                                        />
                                        <span className="min-w-0 flex-1 leading-snug">
                                          {t('nav.instrumentsReport')}
                                        </span>
                                      </Link>
                                    </SheetClose>
                                    <SheetClose asChild>
                                      <Link
                                        href={reportsMaintenanceLogsPath()}
                                        className={mobileRowClass(reportsMaintenanceLogsPath())}
                                      >
                                        <FileText
                                          className={cn(
                                            'size-5 shrink-0',
                                            isNavActive(reportsMaintenanceLogsPath())
                                              ? 'text-primary'
                                              : 'text-muted-foreground',
                                          )}
                                          strokeWidth={
                                            isNavActive(reportsMaintenanceLogsPath()) ? 2.25 : 1.75
                                          }
                                          aria-hidden
                                        />
                                        <span className="min-w-0 flex-1 leading-snug">
                                          {t('nav.maintenanceLogsReport')}
                                        </span>
                                      </Link>
                                    </SheetClose>
                                    <SheetClose asChild>
                                      <Link
                                        href={reportsUsersPath()}
                                        className={mobileRowClass(reportsUsersPath())}
                                      >
                                        <Users
                                          className={cn(
                                            'size-5 shrink-0',
                                            isNavActive(reportsUsersPath())
                                              ? 'text-primary'
                                              : 'text-muted-foreground',
                                          )}
                                          strokeWidth={
                                            isNavActive(reportsUsersPath()) ? 2.25 : 1.75
                                          }
                                          aria-hidden
                                        />
                                        <span className="min-w-0 flex-1 leading-snug">
                                          {t('nav.usersReport')}
                                        </span>
                                      </Link>
                                    </SheetClose>
                                    {canReadCompliance && (
                                      <SheetClose asChild>
                                        <Link
                                          href={complianceDashboardPath()}
                                          className={mobileRowClass(complianceDashboardPath())}
                                        >
                                          <ClipboardCheck
                                            className={cn(
                                              'size-5 shrink-0',
                                              isNavActive(complianceDashboardPath())
                                                ? 'text-primary'
                                                : 'text-muted-foreground',
                                            )}
                                            strokeWidth={
                                              isNavActive(complianceDashboardPath()) ? 2.25 : 1.75
                                            }
                                            aria-hidden
                                          />
                                          <span className="min-w-0 flex-1 leading-snug">
                                            {t('nav.complianceReviews')}
                                          </span>
                                        </Link>
                                      </SheetClose>
                                    )}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            </div>
                          )}

                          {/* Administration */}
                          {showCatalogMenu && (
                            <div className="mt-4 border-t border-border/80 pt-3">
                              <Collapsible open={isAdminOpen} onOpenChange={setIsAdminOpen}>
                                <CollapsibleTrigger asChild>
                                  <button
                                    className={cn(
                                      'flex w-full items-center justify-between px-1 pb-2 text-[0.7rem] font-semibold tracking-[0.12em] uppercase outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded',
                                      isAdministrationActive
                                        ? 'text-primary'
                                        : 'text-muted-foreground',
                                    )}
                                  >
                                    {t('nav.administration')}
                                    <ChevronDown
                                      className={cn(
                                        'size-3.5 shrink-0 transition-transform',
                                        isAdminOpen && 'rotate-180',
                                      )}
                                      aria-hidden
                                    />
                                  </button>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="space-y-0.5">
                                    {/* Organization */}
                                    {organizationLinks.map((item) => {
                                      const Icon = item.icon
                                      const active = isNavActive(item.href)
                                      return (
                                        <SheetClose key={item.href} asChild>
                                          <Link
                                            href={item.href}
                                            className={mobileRowClass(item.href)}
                                          >
                                            <Icon
                                              className={cn(
                                                'size-5 shrink-0',
                                                active ? 'text-primary' : 'text-muted-foreground',
                                              )}
                                              strokeWidth={active ? 2.25 : 1.75}
                                              aria-hidden
                                            />
                                            <span className="min-w-0 flex-1 leading-snug">
                                              {item.label}
                                            </span>
                                          </Link>
                                        </SheetClose>
                                      )
                                    })}

                                    {/* Instrument Types */}
                                    {showCatalogMenu &&
                                      catalogLinks.map((item) => {
                                        const Icon = item.icon
                                        const active = isNavActive(item.href)
                                        return (
                                          <SheetClose key={item.href} asChild>
                                            <Link
                                              href={item.href}
                                              className={mobileRowClass(item.href)}
                                            >
                                              <Icon
                                                className={cn(
                                                  'size-5 shrink-0',
                                                  active ? 'text-primary' : 'text-muted-foreground',
                                                )}
                                                strokeWidth={active ? 2.25 : 1.75}
                                                aria-hidden
                                              />
                                              <span className="min-w-0 flex-1 leading-snug">
                                                {item.label}
                                              </span>
                                            </Link>
                                          </SheetClose>
                                        )
                                      })}

                                    {/* Tags */}
                                    {canReadInstrumentTags && (
                                      <SheetClose asChild>
                                        <Link
                                          href={adminInstrumentTagsPath()}
                                          className={mobileRowClass(adminInstrumentTagsPath())}
                                        >
                                          <Tag
                                            className={cn(
                                              'size-5 shrink-0',
                                              isInstrumentTagsActive
                                                ? 'text-primary'
                                                : 'text-muted-foreground',
                                            )}
                                            strokeWidth={isInstrumentTagsActive ? 2.25 : 1.75}
                                            aria-hidden
                                          />
                                          <span className="min-w-0 flex-1 leading-snug">
                                            {t('nav.instrumentTags')}
                                          </span>
                                        </Link>
                                      </SheetClose>
                                    )}

                                    {/* Users */}
                                    {isAdminOrUserPlus && (
                                      <SheetClose asChild>
                                        <Link
                                          href={adminUsersPath()}
                                          className={cn(
                                            'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[15px] leading-snug transition-colors sm:py-3',
                                            isUsersSectionActive
                                              ? 'bg-primary/10 font-medium text-primary'
                                              : 'text-foreground/90 hover:bg-muted/80 active:bg-muted/90',
                                          )}
                                        >
                                          <Users
                                            className={cn(
                                              'size-5 shrink-0',
                                              isUsersSectionActive
                                                ? 'text-primary'
                                                : 'text-muted-foreground',
                                            )}
                                            strokeWidth={isUsersSectionActive ? 2.25 : 1.75}
                                            aria-hidden
                                          />
                                          <span className="min-w-0 flex-1 leading-snug">
                                            {t('nav.users')}
                                          </span>
                                        </Link>
                                      </SheetClose>
                                    )}

                                    {/* System Settings + Subscription + Sign-off Monitoring */}
                                    {isAdminOrUserPlus && (
                                      <>
                                        <div
                                          className="my-2 h-px w-full max-w-full bg-border/80"
                                          role="separator"
                                        />
                                        {[
                                          {
                                            href: adminSettingsPath(),
                                            label: t('nav.systemSettings'),
                                            icon: Settings,
                                          },
                                          {
                                            href: adminSubscriptionPath(),
                                            label: t('nav.subscription'),
                                            icon: Gauge,
                                          },
                                        ].map((item) => {
                                          const Icon = item.icon
                                          const active = isNavActive(item.href)
                                          return (
                                            <SheetClose key={item.href} asChild>
                                              <Link
                                                href={item.href}
                                                className={mobileRowClass(item.href)}
                                              >
                                                <Icon
                                                  className={cn(
                                                    'size-5 shrink-0',
                                                    active
                                                      ? 'text-primary'
                                                      : 'text-muted-foreground',
                                                  )}
                                                  strokeWidth={active ? 2.25 : 1.75}
                                                  aria-hidden
                                                />
                                                <span className="min-w-0 flex-1 leading-snug">
                                                  {item.label}
                                                </span>
                                              </Link>
                                            </SheetClose>
                                          )
                                        })}
                                        {canReadSignoffChain && (
                                          <SheetClose asChild>
                                            <Link
                                              href={adminSignoffMonitoringPath()}
                                              className={mobileRowClass(
                                                adminSignoffMonitoringPath(),
                                              )}
                                            >
                                              <Activity
                                                className={cn(
                                                  'size-5 shrink-0',
                                                  isNavActive(adminSignoffMonitoringPath())
                                                    ? 'text-primary'
                                                    : 'text-muted-foreground',
                                                )}
                                                strokeWidth={
                                                  isNavActive(adminSignoffMonitoringPath())
                                                    ? 2.25
                                                    : 1.75
                                                }
                                                aria-hidden
                                              />
                                              <span className="min-w-0 flex-1 leading-snug">
                                                {t('nav.signoffMonitoring')}
                                              </span>
                                            </Link>
                                          </SheetClose>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            </div>
                          )}

                          <CustomerSwitcherMobileList />
                        </div>

                        {currentUserInfo && (
                          <div className="-mx-3 border-t border-border/80 px-3 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:-mx-4 sm:px-4">
                            <div className="flex min-h-[2.5rem] items-center gap-3 rounded-lg px-1 py-1">
                              <UserCircle
                                className="size-6 shrink-0 text-muted-foreground"
                                strokeWidth={1.75}
                                aria-hidden
                              />
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-muted-foreground">
                                  {t('nav.signedInAs')}
                                </p>
                                <p className="text-sm font-medium text-foreground [overflow-wrap:anywhere]">
                                  {currentUserInfo.name}
                                </p>
                              </div>
                            </div>
                            <div className="space-y-2 pt-2">
                              {canReadProfile ? (
                                <SheetClose asChild>
                                  <Link
                                    href={profilePath()}
                                    className={mobileRowClass(profilePath(), true)}
                                  >
                                    <UserCircle
                                      className={cn(
                                        'size-5 shrink-0',
                                        isNavActive(profilePath(), true)
                                          ? 'text-primary'
                                          : 'text-muted-foreground',
                                      )}
                                      strokeWidth={isNavActive(profilePath(), true) ? 2.25 : 1.75}
                                      aria-hidden
                                    />
                                    <span className="min-w-0 flex-1 text-left leading-snug">
                                      {t('nav.myProfile')}
                                    </span>
                                  </Link>
                                </SheetClose>
                              ) : null}
                              {canReadNotificationPreferences ? (
                                <SheetClose asChild>
                                  <Link
                                    href={notificationPreferencePath()}
                                    className={mobileRowClass(notificationPreferencePath(), true)}
                                  >
                                    <Bell
                                      className={cn(
                                        'size-5 shrink-0',
                                        isNavActive(notificationPreferencePath(), true)
                                          ? 'text-primary'
                                          : 'text-muted-foreground',
                                      )}
                                      strokeWidth={
                                        isNavActive(notificationPreferencePath(), true)
                                          ? 2.25
                                          : 1.75
                                      }
                                      aria-hidden
                                    />
                                    <span className="min-w-0 flex-1 text-left leading-snug">
                                      {t('nav.notificationPreferences')}
                                    </span>
                                  </Link>
                                </SheetClose>
                              ) : null}
                              {appInfo?.releaseNotesUrl && (
                                <SheetClose asChild>
                                  <a
                                    href={appInfo.releaseNotesUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={cn(
                                      'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[15px] leading-snug transition-colors sm:py-3',
                                      'text-foreground/90 hover:bg-muted/80 active:bg-muted/90',
                                    )}
                                  >
                                    <GitBranch
                                      className="size-5 shrink-0 text-muted-foreground"
                                      strokeWidth={1.75}
                                      aria-hidden
                                    />
                                    <span className="min-w-0 flex-1 text-left leading-snug">
                                      {t('nav.releaseNotes', { version: appInfo.version })}
                                    </span>
                                    <ExternalLink
                                      className="size-3.5 shrink-0 text-muted-foreground"
                                      aria-hidden
                                    />
                                  </a>
                                </SheetClose>
                              )}
                              <SheetClose asChild>
                                {appInfo.contactUsUrl.startsWith('/') ? (
                                  <Link
                                    href={appInfo.contactUsUrl}
                                    className={cn(
                                      'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[15px] leading-snug transition-colors sm:py-3',
                                      'text-foreground/90 hover:bg-muted/80 active:bg-muted/90',
                                    )}
                                  >
                                    <HeadphonesIcon
                                      className="size-5 shrink-0 text-muted-foreground"
                                      strokeWidth={1.75}
                                      aria-hidden
                                    />
                                    <span className="min-w-0 flex-1 text-left leading-snug">
                                      {t('nav.needHelpContactUs')}
                                    </span>
                                  </Link>
                                ) : (
                                  <a
                                    href={appInfo.contactUsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={cn(
                                      'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[15px] leading-snug transition-colors sm:py-3',
                                      'text-foreground/90 hover:bg-muted/80 active:bg-muted/90',
                                    )}
                                  >
                                    <HeadphonesIcon
                                      className="size-5 shrink-0 text-muted-foreground"
                                      strokeWidth={1.75}
                                      aria-hidden
                                    />
                                    <span className="min-w-0 flex-1 text-left leading-snug">
                                      {t('nav.needHelpContactUs')}
                                    </span>
                                    <ExternalLink
                                      className="size-3.5 shrink-0 text-muted-foreground"
                                      aria-hidden
                                    />
                                  </a>
                                )}
                              </SheetClose>
                              {appInfo?.helpDocumentationUrl && (
                                <SheetClose asChild>
                                  <a
                                    href={appInfo.helpDocumentationUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={cn(
                                      'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[15px] leading-snug transition-colors sm:py-3',
                                      'text-foreground/90 hover:bg-muted/80 active:bg-muted/90',
                                    )}
                                  >
                                    <BookOpen
                                      className="size-5 shrink-0 text-muted-foreground"
                                      strokeWidth={1.75}
                                      aria-hidden
                                    />
                                    <span className="min-w-0 flex-1 text-left leading-snug">
                                      {t('nav.helpDocs')}
                                    </span>
                                    <ExternalLink
                                      className="size-3.5 shrink-0 text-muted-foreground"
                                      aria-hidden
                                    />
                                  </a>
                                </SheetClose>
                              )}
                              {appInfo?.companyWebsiteUrl && (
                                <div className="px-3 pb-1 pt-2 text-center">
                                  <a
                                    href={appInfo.companyWebsiteUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                                  >
                                    {t('nav.companySite')}
                                  </a>
                                </div>
                              )}
                              <SheetClose asChild>
                                <button
                                  type="button"
                                  onClick={handleLogout}
                                  disabled={logoutForm.processing}
                                  className="flex w-full items-center justify-center gap-2 rounded-md border-2 border-primary/35 bg-primary/5 px-3 py-2.5 text-sm font-medium text-primary transition-[background-color,box-shadow] hover:bg-primary/10 active:scale-[0.99] disabled:pointer-events-none disabled:opacity-50"
                                >
                                  <LogOut className="size-4 shrink-0" strokeWidth={2} aria-hidden />
                                  {t('nav.signOut')}
                                </button>
                              </SheetClose>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>
        </nav>

        <TenantAdminBanner />
        <ImpersonationBanner />
        <Breadcrumbs />

        <Toaster position="bottom-right" richColors closeButton />

        <main className="mx-auto min-w-0 max-w-7xl px-4 py-6 sm:px-6">{children}</main>
        <SessionTimeoutModal />
      </div>
    </BreadcrumbsProvider>
  )
}

export default AppLayout
